// «Внешний пульс» (Настройки → Система): тревоги шлёт сам сервер — если он ляжет целиком, сообщить некому.
// Поэтому worker после каждой проверки (раз в 5 минут) отмечается у внешнего сервиса (healthchecks.io и т. п.):
// отметок нет дольше периода — сервис сам пишет владельцу. Адрес задаёт владелец: GET/PUT /api/health/ping.
import { Button, Card, Chip, toast, when } from '@autoaff/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api';

interface PingState {
  url: string | null;
  lastAt: string | null;
  lastOkAt: string | null;
  lastError: string | null;
}

/** Отметки раз в 5 минут; старше 15 — worker не стучится (или сервер не работает). */
const STALE_MS = 15 * 60_000;

/** Адрес без секретной части для показа: «hc-ping.com/…1f3a». */
function shortUrl(u: string): string {
  try {
    const x = new URL(u);
    const path = x.pathname.replace(/\/+$/, '');
    return `${x.hostname}${path.length > 6 ? `/…${path.slice(-4)}` : path}`;
  } catch {
    return u;
  }
}

export function PingCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['health-ping'], queryFn: () => api<PingState>('/api/health/ping') });
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const p = q.data;

  const save = async (next: string | null) => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<PingState>('/api/health/ping', { url: next }, 'PUT');
      qc.setQueryData(['health-ping'], r);
      setOpen(false);
      setUrl('');
      toast(next ? 'Адрес сохранён — первая отметка в ближайшие 5 минут' : 'Внешний пульс отключён');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const stale = p?.lastOkAt ? Date.now() - Date.parse(p.lastOkAt) > STALE_MS : false;
  const status = !p?.url ? (
    <Chip>Не подключён</Chip>
  ) : p.lastError ? (
    <Chip tone="danger">Ошибка</Chip>
  ) : !p.lastOkAt ? (
    <Chip tone="info">Ждёт первой отметки</Chip>
  ) : stale ? (
    <Chip tone="warning">Давно не отмечался</Chip>
  ) : (
    <Chip tone="success" dot>
      Работает
    </Chip>
  );

  return (
    <Card title="Внешний пульс" action={q.isPending ? null : status}>
      <p className="muted ping-text">
        Сервер каждые 5 минут отмечается у внешнего сервиса, например healthchecks.io. Если сервер ляжет
        целиком и сам ничего не сможет сообщить, отметки прекратятся — и сервис пришлёт вам письмо или
        сообщение.
      </p>
      {q.isPending ? (
        <div className="skeleton" style={{ height: 18, maxWidth: 420 }} />
      ) : q.isError ? (
        <p className="muted ping-text">Не удалось узнать настройку. {(q.error as Error).message}</p>
      ) : p?.url ? (
        <p className="ping-state">
          <span className="mono">{shortUrl(p.url)}</span>
          {' · '}
          {p.lastError
            ? `последняя отметка не прошла${p.lastAt ? ` ${when(p.lastAt)}` : ''}: сервис ответил «${p.lastError}». Проверьте адрес в кабинете сервиса.`
            : p.lastOkAt
              ? `последняя отметка ${when(p.lastOkAt)}`
              : 'отметок ещё не было'}
        </p>
      ) : null}
      {open ? (
        <form
          className="token-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (url.trim()) void save(url.trim());
          }}
        >
          <label className="label" htmlFor="ping-url">
            Адрес отметки
          </label>
          <input
            id="ping-url"
            className="input"
            type="url"
            inputMode="url"
            autoComplete="off"
            spellCheck={false}
            placeholder="https://hc-ping.com/…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            aria-invalid={error ? true : undefined}
          />
          <p className="muted ping-text">
            В healthchecks.io: создайте проверку с периодом 5 минут и запасом 10, скопируйте её адрес «Ping
            URL». При срочной поломке сервер сам отметит «сбой» — письмо придёт сразу.
          </p>
          {error && (
            <p className="notice error" style={{ margin: 0 }}>
              {error}
            </p>
          )}
          <div className="row">
            <Button type="submit" variant="primary" disabled={!url.trim() || busy}>
              {busy ? 'Сохраняю…' : 'Сохранить адрес'}
            </Button>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Отмена
            </Button>
          </div>
        </form>
      ) : (
        !q.isPending && (
          <div className="flex gap-2 flex-wrap" style={{ marginTop: 12 }}>
            <Button size="sm" variant={p?.url ? 'default' : 'primary'} onClick={() => setOpen(true)}>
              {p?.url ? 'Заменить адрес' : 'Подключить'}
            </Button>
            {p?.url && (
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => void save(null)}>
                Отключить
              </Button>
            )}
          </div>
        )
      )}
    </Card>
  );
}
